import React from 'react';
import { Box, Text } from 'ink';
import { supportsHyperlinks, hyperlink } from '../hyperlinks';
import { useTheme } from '../theme';

export interface LinkProps {
  /** The URL to link to. */
  url: string;
  /** Display text. Defaults to the URL itself. */
  children?: string;
  /** Ink Text color. Defaults to the theme's primary accent. */
  color?: string;
}

/**
 * Ink component that renders an OSC 8 clickable hyperlink when the terminal
 * supports it, or falls back to showing the URL as plain text.
 */
export const Link: React.FC<LinkProps> = ({ url, children, color: colorProp }) => {
  const { tokens } = useTheme();
  const color = colorProp ?? tokens.text.accent;
  const displayText = children ?? url;

  if (supportsHyperlinks()) {
    // Render the display text wrapped in OSC 8 escape sequences.
    // Ink preserves OSC sequences through its renderer.
    const linked = hyperlink(displayText, url);
    return <Text color={color}>{linked}</Text>;
  }

  // Fallback with distinct display text: lay out the label and the URL as two
  // non-shrinking flex items in a wrapping row. The URL stays inline after the
  // label when there is room, and drops onto its own line (intact) when there
  // is not — instead of being hard-wrapped mid-word, which would corrupt it for
  // copy/paste. On terminals narrower than the URL itself the URL box overflows
  // and the terminal soft-wraps it, which preserves the characters.
  if (displayText !== url) {
    return (
      <Box flexWrap="wrap">
        <Box flexShrink={0}>
          <Text>{displayText} </Text>
        </Box>
        <Box flexShrink={0}>
          <Text color={color}>({url})</Text>
        </Box>
      </Box>
    );
  }

  return <Text color={color}>{url}</Text>;
};
